import { useState, useCallback, useMemo } from "react";
import {
  ShieldCheck,
  ArrowLeft,
  Tag,
  AlertCircle,
  FileText,
  CreditCard,
  Sparkles,
  CheckCircle2,
  Eye,
} from "lucide-react";
import ServiceForm from "./ServiceForm";
import NINSlipSamplePreview from "./components/NINSlipSamplePreview";

// ─── Default NIN Sub-Services Fallback Definition ─────────────────────────────
// const DEFAULT_NIN_SUB_SERVICES = [
//   {
//     id: "nin-standard-slip",
//     slug: "nin-standard-slip",
//     name: "NIN Standard Slip",
//     cost: "200.00",
//     badge: "Standard Slip",
//     additional_payload_meta: {
//       description: "Standard government NIN slip download with barcode verification and citizen bio-data.",
//     },
//   },
//   {
//     id: "nin-personal-slip",
//     slug: "nin-personal-slip",
//     name: "Personal Information Slip",
//     cost: "200.00",
//     badge: "Bio Slip",
//     additional_payload_meta: {
//       description: "Personal identification slip with demographic details, photograph, and digital signature.",
//     },
//   },
//   {
//     id: "nin-premium-slip",
//     slug: "nin-premium-slip",
//     name: "NIN Premium Slip",
//     cost: "200.00",
//     badge: "Premium Gloss",
//     additional_payload_meta: {
//       description: "High-resolution premium gloss certificate layout with security guilloche pattern and QR verification.",
//     },
//   },
//   {
//     id: "nin-digital-card",
//     slug: "nin-digital-card",
//     name: "NIN Digital ID Card",
//     cost: "500.00",
//     badge: "4K Wallet Card",
//     additional_payload_meta: {
//       description: "Dual-sided wallet-sized digital identity card (CR80 print dimensions with front citizen profile and back disclaimer).",
//     },
//   },
// ];

// ─── Price Tag ────────────────────────────────────────────────────────────────
const PriceTag = ({ cost, suffix = "per request" }) => (
  <div className="cht-inline-flex cht-items-center cht-gap-1.5 cht-bg-[#f2f4fd] cht-text-[#3e57da] cht-rounded-xl cht-px-3 cht-py-1.5">
    <Tag size={14} />
    <span className="cht-text-sm cht-font-bold">
      ₦{parseFloat(cost).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
    </span>
    <span className="cht-text-xs cht-text-[#3e57da]/70">{suffix}</span>
  </div>
);

// ─── Service Detail View (Replaces Execution Modal) ───────────────────────────
const ServiceDetailView = ({
  service,
  selectedSubService,
  setSelectedSubService,
  onBack,
}) => {
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (logId) => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("access");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/verification/download/${logId}/pdf/`,
        {
          headers: { Authorization: `JWT ${token}` },
        },
      );
      if (!res.ok) {
        throw new Error("Failed to download PDF");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `verification_report_${logId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const isSandbox = service.environment === "SANDBOX";

  const isNin = useMemo(() => {
    return Boolean(
      service.slug?.toLowerCase().includes("nin") ||
      service.name?.toLowerCase().includes("nin") ||
      service.category === "Identity"
    );
  }, [service]);

  // Dynamic pricing calculation based on current selection matrix
  const currentCost = useMemo(() => {
    if (isSandbox) return "0.00";
    return selectedSubService ? selectedSubService.cost : service.cost;
  }, [selectedSubService, service.cost, isSandbox]);

  const handleFieldChange = useCallback((name, value) => {
    setFieldValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors([]);
  }, []);

  const handleSelectVariantBySlug = useCallback((slug) => {
    const list = Array.isArray(service.sub_services) ? service.sub_services : [];
    const found = list.find((s) => s.slug === slug);
    if (found) {
      setSelectedSubService(found);
    } else {
      setSelectedSubService({ slug, name: slug });
    }
  }, [service.sub_services, setSelectedSubService]);

  // Compute complete available variants (ensuring all 4 NIN types exist for Identity/NIN services)
  const availableSubServices = useMemo(() => {
    const list = Array.isArray(service.sub_services)
      ? [...service.sub_services]
      : [];
    const isNin =
      service.slug?.toLowerCase().includes("nin") ||
      service.name?.toLowerCase().includes("nin") ||
      service.category === "Identity";

    // if (isNin) {
    //   DEFAULT_NIN_SUB_SERVICES.forEach((def) => {
    //     const idx = list.findIndex((s) => s.slug === def.slug);
    //     if (idx === -1) {
    //       list.push(def);
    //     } else {
    //       list[idx] = {
    //         ...def,
    //         ...list[idx],
    //         name: list[idx].name || def.name,
    //         cost: list[idx].cost || def.cost,
    //         badge: def.badge,
    //         additional_payload_meta: {
    //           ...def.additional_payload_meta,
    //           ...(list[idx].additional_payload_meta || {}),
    //         },
    //       };
    //     }
    //   });
    // }
    return list;
  }, [service]);

  // Variant iconography generator for Antigravity aesthetic depth
  const getSubServiceIcon = (slug = "") => {
    const s = slug.toLowerCase();
    if (s.includes("card") || s.includes("digital"))
      return <CreditCard size={18} />;
    if (s.includes("premium") || s.includes("gloss"))
      return <Sparkles size={18} />;
    if (s.includes("personal")) return <FileText size={18} />;
    if (s.includes("standard") || s.includes("slip") || s.includes("download"))
      return <FileText size={18} />;
    return <CreditCard size={18} />;
  };

  const getSubServiceBadge = (slug = "", subBadge) => {
    if (subBadge) return subBadge;
    const s = slug.toLowerCase();
    if (s.includes("card") || s.includes("digital")) return "4K Wallet Card";
    if (s.includes("premium") || s.includes("gloss")) return "Premium Gloss";
    if (s.includes("personal")) return "Bio Slip";
    if (s.includes("standard")) return "Standard Slip";
    return null;
  };

  const getSubServiceDescription = (sub) => {
    if (sub?.additional_payload_meta?.description) {
      return sub.additional_payload_meta.description;
    }
    const s = (sub?.slug || "").toLowerCase();
    if (s.includes("card") || s.includes("digital")) {
      return "Dual-sided wallet-sized digital identity card (CR80 print dimensions with front citizen profile and back disclaimer).";
    }
    if (s.includes("premium")) {
      return "High-resolution premium gloss certificate layout with security guilloche pattern and QR verification.";
    }
    if (s.includes("personal")) {
      return "Personal identification slip with demographic details, photograph, and digital signature.";
    }
    if (s.includes("standard")) {
      return "Standard government NIN slip download with barcode verification and citizen bio-data.";
    }
    return "Process as individual output format tier";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    setFieldErrors([]);

    try {
      const schema = service.input_schema || [];
      const hasFile = schema.some((f) => f.type === "file");
      const token = localStorage.getItem("access");

      let res;
      if (hasFile) {
        const form = new FormData();
        form.append("service_slug", service.slug);
        if (selectedSubService) {
          form.append("sub_service_slug", selectedSubService.slug);
        }
        schema.forEach(({ name }) => {
          const val = fieldValues[name];
          if (val !== undefined && val !== null) form.append(name, val);
        });

        res = await fetch(
          `${process.env.REACT_APP_API_URL}/verification/execute/`,
          {
            method: "POST",
            headers: { Authorization: `JWT ${token}` },
            body: form,
          },
        );
      } else {
        const payload = {};
        schema.forEach(({ name }) => {
          if (fieldValues[name] !== undefined)
            payload[name] = fieldValues[name];
        });
        // 🟢 Append sub_service_slug parameters directly into the JSON configuration body if selected
        if (selectedSubService) {
          payload["sub_service_slug"] = selectedSubService.slug;
        }

        res = await fetch(
          `${process.env.REACT_APP_API_URL}/verification/execute/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `JWT ${token}`,
            },
            body: JSON.stringify({ service_slug: service.slug, payload }),
          },
        );
      }

      const data = await res.json();
      if (res.status === 422 && data.fields) {
        setFieldErrors(data.fields);
      } else {
        setResult({ ok: res.ok, data });
      }
    } catch {
      setResult({
        ok: false,
        data: { message: "Network structural error. Please try again." },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cht-animate-in cht-fade-in cht-slide-in-from-bottom-4 cht-duration-500">
      {/* Detail view header navigator block */}
      <button
        onClick={onBack}
        className="cht-mb-6 cht-flex cht-items-center cht-gap-2 cht-text-sm cht-font-semibold cht-text-gray-600 hover:cht-text-[#3e57da] cht-transition-colors cht-group"
      >
        <ArrowLeft
          size={16}
          className="group-hover:-cht-translate-x-1 cht-transition-transform"
        />{" "}
        Back to Services
      </button>

      <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8 cht-items-start">
        {/* LEFT COLUMN: Service Profile & Sub-Service Variant Grid Selection Matrix */}
        <div className="lg:cht-col-span-5 cht-space-y-6">
          <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-p-6 cht-border cht-border-gray-100">
            <span className="cht-text-xs cht-px-2.5 cht-py-1 cht-bg-gray-100 cht-text-gray-600 cht-rounded-full cht-font-medium">
              {service.category}
            </span>
            <h2 className="cht-text-2xl cht-font-bold cht-text-gray-900 cht-mt-3">
              {service.name}
            </h2>

            <div className="cht-mt-4 cht-flex cht-items-center cht-gap-3">
              <PriceTag
                cost={currentCost}
                suffix={isSandbox ? "Test Mode" : "Selected Tier"}
              />
            </div>
          </div>

          {availableSubServices && availableSubServices.length > 0 && (
            <div className="cht-space-y-3">
              <h4 className="cht-text-sm cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-wider">
                Select Processing Variation
              </h4>
              <div className="cht-grid cht-grid-cols-1 cht-gap-3">
                {/* Fallback to Default option option node card */}
                <div
                  onClick={() => setSelectedSubService(null)}
                  className={`cht-p-4 cht-rounded-squircle cht-border cht-cursor-pointer cht-transition-all cht-flex cht-items-center cht-justify-between cht-bg-white ${
                    selectedSubService === null
                      ? "cht-border-[#3e57da] cht-shadow-soft-blue cht-bg-[#3e57da]/5"
                      : "cht-border-gray-100 hover:cht-border-gray-300"
                  }`}
                >
                  <div className="cht-flex cht-items-start cht-gap-3 cht-min-w-0">
                    <div
                      className={`cht-p-2.5 cht-rounded-xl cht-shrink-0 cht-mt-0.5 ${
                        selectedSubService === null
                          ? "cht-bg-[#3e57da] cht-text-white"
                          : "cht-bg-gray-100 cht-text-gray-500"
                      }`}
                    >
                      <ShieldCheck size={18} />
                    </div>
                    <div className="cht-min-w-0">
                      <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap cht-mb-1">
                        <p className="cht-text-sm cht-font-bold cht-text-gray-900">
                          Standard Base Check
                        </p>
                        <span className="cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-full cht-bg-gray-100 cht-text-gray-600">
                          Raw Data
                        </span>
                      </div>
                      <p className="cht-text-xs cht-text-gray-500 cht-leading-relaxed">
                        Run normal gateway search verification without custom
                        printable slip formatting
                      </p>
                    </div>
                  </div>
                  <div className="cht-text-right cht-shrink-0 cht-ml-3">
                    <span className="cht-text-sm cht-font-bold cht-text-gray-900 cht-block">
                      {isSandbox
                        ? "₦0.00"
                        : `₦${parseFloat(service.cost).toFixed(2)}`}
                    </span>
                    {selectedSubService === null && (
                      <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-mt-1">
                        <CheckCircle2 size={12} /> Active
                      </span>
                    )}
                  </div>
                </div>

                {/* Sub services array mapping iterations */}
                {availableSubServices.map((sub) => {
                  const isSelected =
                    selectedSubService?.slug === sub.slug ||
                    (selectedSubService?.id &&
                      selectedSubService.id === sub.id);
                  const badge = getSubServiceBadge(sub.slug, sub.badge);
                  const description = getSubServiceDescription(sub);

                  return (
                    <div
                      key={sub.slug || sub.id}
                      onClick={() => setSelectedSubService(sub)}
                      className={`cht-p-4 cht-rounded-squircle cht-border cht-cursor-pointer cht-transition-all cht-flex cht-items-center cht-justify-between cht-bg-white ${
                        isSelected
                          ? "cht-border-[#3e57da] cht-shadow-soft-blue cht-bg-[#3e57da]/5"
                          : "cht-border-gray-100 hover:cht-border-gray-300"
                      }`}
                    >
                      <div className="cht-flex cht-items-start cht-gap-3 cht-min-w-0">
                        <div
                          className={`cht-p-2.5 cht-rounded-xl cht-shrink-0 cht-mt-0.5 ${
                            isSelected
                              ? "cht-bg-[#3e57da] cht-text-white"
                              : "cht-bg-gray-100 cht-text-gray-500"
                          }`}
                        >
                          {getSubServiceIcon(sub.slug)}
                        </div>
                        <div className="cht-min-w-0">
                          <div className="cht-flex cht-items-center cht-gap-2 cht-flex-wrap cht-mb-1">
                            <p className="cht-text-sm cht-font-bold cht-text-gray-900">
                              {sub.name}
                            </p>
                            {badge && (
                              <span
                                className={`cht-text-[10px] cht-font-bold cht-px-2 cht-py-0.5 cht-rounded-full ${
                                  sub.slug.includes("card") ||
                                  sub.slug.includes("digital")
                                    ? "cht-bg-indigo-50 cht-text-indigo-600 cht-border cht-border-indigo-200"
                                    : sub.slug.includes("premium")
                                      ? "cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200"
                                      : "cht-bg-blue-50 cht-text-[#3e57da] cht-border cht-border-blue-100"
                                }`}
                              >
                                {badge}
                              </span>
                            )}
                          </div>
                          <p className="cht-text-xs cht-text-gray-500 cht-leading-relaxed">
                            {description}
                          </p>
                        </div>
                      </div>
                      <div className="cht-text-right cht-shrink-0 cht-ml-3">
                        <span className="cht-text-sm cht-font-bold cht-text-gray-900 cht-block">
                          {isSandbox
                            ? "₦0.00"
                            : `₦${parseFloat(sub.cost).toFixed(2)}`}
                        </span>
                        {isSelected && (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-text-[10px] cht-font-bold cht-text-[#3e57da] cht-mt-1">
                            <CheckCircle2 size={12} /> Active
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 🟢 Interactive NIN Document Specimen Sample Preview */}
          {isNin && (
            <NINSlipSamplePreview
              selectedSlug={selectedSubService?.slug}
              onSelectVariant={handleSelectVariantBySlug}
              isSandbox={isSandbox}
            />
          )}
        </div>

        {/* RIGHT COLUMN: Execution Payload Input Form Elements & Asynchronous Responses */}
        <div className="lg:cht-col-span-7">
          <div className="cht-bg-white cht-rounded-squircle cht-shadow-soft cht-p-6 cht-border cht-border-gray-100">
            <h3 className="cht-text-base cht-font-bold cht-text-gray-900 cht-mb-4 cht-flex cht-items-center cht-gap-2">
              Execution Payload Definition
            </h3>

            <form onSubmit={handleSubmit} className="cht-space-y-4">
              <ServiceForm
                schema={service.input_schema || []}
                values={fieldValues}
                onChange={handleFieldChange}
              />

              {fieldErrors.length > 0 && (
                <div className="cht-bg-red-50 cht-border cht-border-red-100 cht-rounded-xl cht-p-4">
                  <div className="cht-flex cht-items-center cht-gap-2 cht-mb-2 cht-text-red-700 cht-font-semibold cht-text-sm">
                    <AlertCircle size={15} /> Input Validation Errors
                  </div>
                  <ul className="cht-list-disc cht-list-inside cht-text-sm cht-text-red-600 cht-space-y-1">
                    {fieldErrors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="cht-w-full cht-py-3 cht-bg-[#3e57da] cht-text-white cht-rounded-xl cht-text-sm cht-font-semibold hover:cht-bg-[#3248c0] cht-transition-all hover:cht-shadow-soft-blue disabled:cht-opacity-60 disabled:cht-cursor-not-allowed"
              >
                {loading
                  ? "Executing Secure Handshake…"
                  : `Confirm & Pay ₦${parseFloat(currentCost).toLocaleString("en-NG", { minimumFractionDigits: 2 })} →`}
              </button>
            </form>

            {result && result.ok && (
              <div className="cht-mt-6 cht-rounded-xl cht-p-5 cht-bg-green-50 cht-border cht-border-green-100 cht-text-green-800">
                <div className="cht-flex cht-justify-between cht-items-center cht-mb-4">
                  <div>
                    <h4 className="cht-text-sm cht-font-bold">
                      Transaction Confirmed Successfully
                    </h4>
                    <p className="cht-text-xs cht-text-green-700/80">
                      Your layout documents have been compiled and sealed.
                    </p>
                  </div>

                  {/* 🟢 NEW: Actionable Download Trigger Node */}
                  {result.data.log_id && (
                    <button
                      onClick={() => handleDownload(result.data.log_id)}
                      disabled={downloading}
                      className="cht-px-4 cht-py-2 cht-bg-green-600 cht-text-white cht-font-semibold cht-text-xs cht-rounded-lg hover:cht-bg-green-700 disabled:cht-opacity-50 cht-transition-all cht-shadow-sm cht-inline-flex cht-items-center cht-gap-1.5"
                    >
                      {downloading && (
                        <span className="cht-animate-spin cht-h-3 cht-w-3 cht-border-2 cht-border-white cht-border-t-transparent cht-rounded-full" />
                      )}
                      {downloading ? "Downloading..." : "Download Document ↓"}
                    </button>
                  )}
                </div>

                <pre className="cht-whitespace-pre-wrap cht-break-words cht-text-xs cht-font-mono cht-bg-black/5 cht-p-3 cht-rounded-lg">
                  {JSON.stringify(result.data.data, null, 2)}
                </pre>
              </div>
            )}

            {result && !result.ok && (
              <div className="cht-mt-6 cht-rounded-xl cht-p-5 cht-bg-red-50 cht-border cht-border-red-100 cht-text-red-800 cht-animate-in cht-fade-in cht-duration-300">
                <h4 className="cht-text-sm cht-font-bold cht-mb-2">
                  Execution Failure Block
                </h4>
                <pre className="cht-whitespace-pre-wrap cht-break-words cht-text-xs cht-font-mono cht-bg-black/5 cht-p-3 cht-rounded-lg cht-max-h-64 cht-overflow-y-auto">
                  {JSON.stringify(result.data, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailView;
